import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  format,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  addDays,
  parseISO,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  getDay,
  isSameDay,
  isSameMonth,
  isToday,
} from "date-fns";
import { ChevronLeft, ChevronRight, Clock, User, ArrowRightLeft, Hand, Loader2, Trash2, UserMinus, MessageSquare, Send, X as XIcon, Copy, Pencil, Plus, Minus, Users } from "lucide-react";

const REMOVAL_REASONS = [
  { value: "Call Out Sick", label: "Call Out Sick" },
  { value: "Call Out Family Emergency", label: "Call Out Family Emergency" },
  { value: "Administrative", label: "Administrative" },
];

const CompanySchedule = () => {
  const { user, getAuthHeaders, canManage, scheduleUpdate } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [groupedShifts, setGroupedShifts] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedShift, setSelectedShift] = useState(null);
  const [showTradeDialog, setShowTradeDialog] = useState(false);
  const [showPickupDialog, setShowPickupDialog] = useState(false);
  const [selectedMyShift, setSelectedMyShift] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteShiftId, setDeleteShiftId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { shiftId, empName, position, x, y }
  const [showRemoveSubmenu, setShowRemoveSubmenu] = useState(false);
  const [removingShiftId, setRemovingShiftId] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignShiftInfo, setAssignShiftInfo] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notesShiftIds, setNotesShiftIds] = useState([]);
  const [notesEventName, setNotesEventName] = useState("");
  const [shiftNotes, setShiftNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState("");
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyTargetWeeks, setCopyTargetWeeks] = useState(1);
  const [copying, setCopying] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [monthlyCounts, setMonthlyCounts] = useState({});
  const [showDeleteEventDialog, setShowDeleteEventDialog] = useState(false);
  const [deleteEventInfo, setDeleteEventInfo] = useState(null);
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [showEditEventDialog, setShowEditEventDialog] = useState(false);
  const [editEventInfo, setEditEventInfo] = useState(null);
  const [editForm, setEditForm] = useState({ event_name: "", start_time: "", end_time: "", position_slots: [] });
  const [savingEvent, setSavingEvent] = useState(false);
  const [allPositions, setAllPositions] = useState([]);

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });

  useEffect(() => {
    fetchData();
  }, [currentDate, scheduleUpdate]);

  useEffect(() => {
    fetchMonthlyCounts();
  }, [calendarMonth, scheduleUpdate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const start = format(weekStart, "yyyy-MM-dd");
      const end = format(weekEnd, "yyyy-MM-dd");

      const [groupedRes, myShiftsRes] = await Promise.all([
        axios.get(`${API}/shifts/grouped?start_date=${start}&end_date=${end}`, {
          headers: getAuthHeaders(),
          
        }),
        axios.get(`${API}/shifts/my?start_date=${start}&end_date=${end}`, {
          headers: getAuthHeaders(),
          
        }),
      ]);

      setGroupedShifts(groupedRes.data);
      setMyShifts(myShiftsRes.data);
    } catch (error) {
      console.error("Error fetching shifts:", error);
    } finally {
      setLoading(false);
    }
  };

  const getShiftsForDate = (dateStr) => {
    return groupedShifts.filter((s) => s.date === dateStr);
  };

  const fetchMonthlyCounts = async () => {
    try {
      const mStart = format(startOfMonth(calendarMonth), "yyyy-MM-dd");
      const mEnd = format(endOfMonth(calendarMonth), "yyyy-MM-dd");
      const res = await axios.get(`${API}/shifts/monthly-counts?start_date=${mStart}&end_date=${mEnd}`, { headers: getAuthHeaders() });
      setMonthlyCounts(res.data);
    } catch (error) {
      console.error("Error fetching monthly counts:", error);
    }
  };

  const buildCalendarDays = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const startDay = getDay(monthStart);
    const days = [];
    // Pad start with empty cells
    for (let i = 0; i < startDay; i++) days.push(null);
    let d = monthStart;
    while (d <= monthEnd) {
      days.push(new Date(d));
      d = addDays(d, 1);
    }
    return days;
  };

  const handleCopyWeek = async () => {
    setCopying(true);
    try {
      const sourceStart = format(weekStart, "yyyy-MM-dd");
      for (let i = 0; i < copyTargetWeeks; i++) {
        const targetWeek = addWeeks(weekStart, i + 1);
        const targetStart = format(targetWeek, "yyyy-MM-dd");
        await axios.post(`${API}/schedule/copy-week`, {
          source_start: sourceStart,
          target_start: targetStart,
        }, { headers: getAuthHeaders() });
      }
      toast.success(`Schedule copied to ${copyTargetWeeks} future week${copyTargetWeeks > 1 ? "s" : ""}`);
      setShowCopyDialog(false);
      setCopyTargetWeeks(1);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to copy schedule");
    } finally {
      setCopying(false);
    }
  };

  const handleOpenShiftClick = (event, shiftId) => {
    if (canManage) {
      // Admin/Supervisor: show assign dialog
      const shiftData = event.all_shifts?.find(s => s.id === shiftId);
      setAssignShiftInfo({ ...event, shiftId, positions: shiftData?.positions_needed || [] });
      setSelectedEmployee("");
      setSuggestions([]);
      setShowAssignDialog(true);
      fetchEmployeesAndSuggestions(shiftId);
    } else {
      // Employee: show pickup request dialog
      setSelectedShift({ ...event, openShiftId: shiftId });
      setShowPickupDialog(true);
    }
  };

  const fetchEmployeesAndSuggestions = async (shiftId) => {
    setLoadingSuggestions(true);
    try {
      const [empRes, sugRes] = await Promise.all([
        axios.get(`${API}/employees`, { headers: getAuthHeaders() }),
        axios.get(`${API}/availability/suggest/${shiftId}`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
      ]);
      setAllEmployees(empRes.data);
      setSuggestions(sugRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAssignEmployee = async () => {
    if (!selectedEmployee || !assignShiftInfo?.shiftId) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/shifts/${assignShiftInfo.shiftId}/assign?employee_id=${selectedEmployee}`,
        {},
        { headers: getAuthHeaders() }
      );
      if (res.data.conflicts && res.data.conflicts.length > 0) {
        toast.warning(`Assigned with conflicts: ${res.data.conflicts.map(c => c.detail).join(", ")}`, { duration: 6000 });
      } else {
        toast.success("Employee assigned to shift");
      }
      setShowAssignDialog(false);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to assign employee");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmployeeShiftClick = (event, shiftData) => {
    if (shiftData.assigned_to === user?.id) {
      toast.info("This is your shift");
      return;
    }
    setSelectedShift({ ...event, targetShift: shiftData });
    setShowTradeDialog(true);
  };

  const handlePickupRequest = async () => {
    if (!selectedShift?.openShiftId) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/shift-pickup-requests`,
        { shift_id: selectedShift.openShiftId },
        { headers: getAuthHeaders() }
      );
      toast.success("Pickup request submitted! Awaiting approval.");
      setShowPickupDialog(false);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to submit pickup request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTradeSubmit = async () => {
    if (!selectedMyShift || !selectedShift?.targetShift) {
      toast.error("Please select one of your shifts to trade");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/trade-requests`,
        {
          from_shift_id: selectedMyShift,
          to_shift_id: selectedShift.targetShift.shift_id,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Trade request submitted!");
      setShowTradeDialog(false);
      setSelectedMyShift("");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to submit trade request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteShift = async () => {
    if (!deleteShiftId) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API}/shifts/${deleteShiftId}`, {
        headers: getAuthHeaders(),
        
      });
      toast.success("Shift deleted");
      setShowDeleteDialog(false);
      setDeleteShiftId(null);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to delete shift");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!deleteEventInfo) return;
    setDeletingEvent(true);
    try {
      const res = await axios.post(`${API}/events/delete`, {
        event_name: deleteEventInfo.event_name,
        date: deleteEventInfo.date,
        start_time: deleteEventInfo.start_time,
        end_time: deleteEventInfo.end_time,
      }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setShowDeleteEventDialog(false);
      setDeleteEventInfo(null);
      fetchData();
      fetchMonthlyCounts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to delete event");
    } finally {
      setDeletingEvent(false);
    }
  };

  const openEditEventDialog = async (event) => {
    // Build position slot counts and track assigned employees from all_shifts
    const positionCounts = {};
    const positionEmployees = {}; // { "Tour Guide": [{shift_id, name, assigned_to}, ...] }
    for (const shift of (event.all_shifts || [])) {
      const pos = shift.positions_needed?.[0] || "General";
      positionCounts[pos] = (positionCounts[pos] || 0) + 1;
      if (!positionEmployees[pos]) positionEmployees[pos] = [];
      // Find employee name from employees_by_position
      let empName = null;
      if (shift.assigned_to) {
        const posEmps = event.employees_by_position?.[pos] || [];
        const found = posEmps.find(e => e.shift_id === shift.id);
        empName = found?.name || shift.assigned_name || null;
      }
      positionEmployees[pos].push({
        shift_id: shift.id,
        assigned_to: shift.assigned_to,
        name: empName,
      });
    }

    // Fetch all available positions
    let positions = allPositions;
    if (positions.length === 0) {
      try {
        const res = await axios.get(`${API}/positions`, { headers: getAuthHeaders() });
        positions = res.data;
        setAllPositions(res.data);
      } catch (e) {
        positions = [];
      }
    }

    // Build position_slots
    const slots = positions.map(p => ({
      position: p.name,
      count: positionCounts[p.name] || 0,
      employees: positionEmployees[p.name] || [],
      // Track which shift_ids to keep (start with all)
      keepIds: (positionEmployees[p.name] || []).map(e => e.shift_id),
    }));
    for (const pos of Object.keys(positionCounts)) {
      if (!slots.find(s => s.position === pos)) {
        slots.push({
          position: pos,
          count: positionCounts[pos],
          employees: positionEmployees[pos] || [],
          keepIds: (positionEmployees[pos] || []).map(e => e.shift_id),
        });
      }
    }

    setEditEventInfo({
      event_name: event.event_name,
      date: event.date,
      start_time: event.start_time,
      end_time: event.end_time,
      total_shifts: event.all_shifts?.length || 0,
    });
    setEditForm({
      event_name: event.event_name,
      start_time: event.start_time,
      end_time: event.end_time,
      position_slots: slots,
    });
    setShowEditEventDialog(true);
  };

  const handleEditPositionCount = (positionName, delta) => {
    setEditForm(prev => ({
      ...prev,
      position_slots: prev.position_slots.map(slot => {
        if (slot.position !== positionName) return slot;
        const newCount = Math.max(0, slot.count + delta);
        // If reducing below # of assigned employees, need to pick who stays
        return { ...slot, count: newCount };
      }),
    }));
  };

  const setEditPositionCount = (positionName, count) => {
    setEditForm(prev => ({
      ...prev,
      position_slots: prev.position_slots.map(slot =>
        slot.position === positionName ? { ...slot, count: Math.max(0, parseInt(count) || 0) } : slot
      ),
    }));
  };

  const toggleKeepEmployee = (positionName, shiftId) => {
    setEditForm(prev => ({
      ...prev,
      position_slots: prev.position_slots.map(slot => {
        if (slot.position !== positionName) return slot;
        const keepIds = slot.keepIds.includes(shiftId)
          ? slot.keepIds.filter(id => id !== shiftId)
          : [...slot.keepIds, shiftId];
        return { ...slot, keepIds };
      }),
    }));
  };

  const getEditTotalSlots = () => editForm.position_slots.reduce((sum, s) => sum + s.count, 0);
  const getEditActivePositions = () => editForm.position_slots.filter(s => s.count > 0);

  const handleSaveEvent = async () => {
    if (!editEventInfo) return;
    if (!editForm.event_name.trim()) {
      toast.error("Event name is required");
      return;
    }
    if (!editForm.start_time || !editForm.end_time) {
      toast.error("Start and end times are required");
      return;
    }

    // Validate: for positions being reduced, check enough employees are deselected
    for (const slot of editForm.position_slots) {
      const assignedEmps = slot.employees?.filter(e => e.assigned_to) || [];
      const keptAssigned = assignedEmps.filter(e => slot.keepIds.includes(e.shift_id));
      if (keptAssigned.length > slot.count) {
        toast.error(`Too many employees kept for ${slot.position}. Please deselect ${keptAssigned.length - slot.count} more.`);
        return;
      }
    }

    setSavingEvent(true);
    try {
      // Collect all shift IDs that should be kept
      const keepShiftIds = [];
      for (const slot of editForm.position_slots) {
        if (slot.count > 0) {
          keepShiftIds.push(...slot.keepIds.slice(0, slot.count));
        }
      }

      const res = await axios.post(`${API}/events/update`, {
        current_event_name: editEventInfo.event_name,
        current_date: editEventInfo.date,
        current_start_time: editEventInfo.start_time,
        current_end_time: editEventInfo.end_time,
        new_event_name: editForm.event_name,
        new_start_time: editForm.start_time,
        new_end_time: editForm.end_time,
        position_slots: getEditActivePositions().map(s => ({ position: s.position, count: s.count })),
        keep_shift_ids: keepShiftIds,
      }, { headers: getAuthHeaders() });

      toast.success(res.data.message);
      setShowEditEventDialog(false);
      setEditEventInfo(null);
      fetchData();
      fetchMonthlyCounts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to update event");
    } finally {
      setSavingEvent(false);
    }
  };

  const handleEmployeeClick = (e, shiftId, empName, position) => {
    if (!canManage) return;
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      shiftId,
      empName,
      position,
      x: rect.left,
      y: rect.bottom + 4,
    });
    setShowRemoveSubmenu(false);
  };

  const handleRemoveFromTour = async (reason) => {
    if (!contextMenu?.shiftId) return;
    setRemovingShiftId(contextMenu.shiftId);
    try {
      const res = await axios.post(
        `${API}/shifts/${contextMenu.shiftId}/remove-employee?reason=${encodeURIComponent(reason)}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      setContextMenu(null);
      setShowRemoveSubmenu(false);
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to remove employee");
    } finally {
      setRemovingShiftId(null);
    }
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu) {
        setContextMenu(null);
        setShowRemoveSubmenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [contextMenu]);

  const openNotesDialog = async (event) => {
    const shiftIds = (event.all_shifts || []).map(s => s.id);
    setNotesShiftIds(shiftIds);
    setNotesEventName(`${event.event_name} — ${event.date}`);
    setShowNotesDialog(true);
    setNewNoteText("");
    setLoadingNotes(true);
    try {
      const allNotes = [];
      for (const sid of shiftIds) {
        const res = await axios.get(`${API}/shifts/${sid}/notes`, { headers: getAuthHeaders() });
        allNotes.push(...res.data);
      }
      // Deduplicate by id and sort by date
      const unique = Object.values(Object.fromEntries(allNotes.map(n => [n.id, n])));
      unique.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setShiftNotes(unique);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim() || notesShiftIds.length === 0) return;
    setAddingNote(true);
    try {
      // Add note to the first shift in the group
      const res = await axios.post(
        `${API}/shifts/${notesShiftIds[0]}/notes`,
        { text: newNoteText.trim() },
        { headers: getAuthHeaders() }
      );
      setShiftNotes(prev => [res.data, ...prev]);
      setNewNoteText("");
      toast.success("Note added");
      fetchData(); // refresh to update notes_count
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId, shiftId) => {
    try {
      await axios.delete(`${API}/shifts/${shiftId || notesShiftIds[0]}/notes/${noteId}`, { headers: getAuthHeaders() });
      setShiftNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success("Note deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete note");
    }
  };

  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const day = addDays(weekStart, i);
    weekDays.push({
      date: format(day, "yyyy-MM-dd"),
      dayName: format(day, "EEEE"),
      dayDate: format(day, "MMM d"),
    });
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="company-schedule-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Company Schedule</h1>
          <p className="text-[#666666]">
            Week of {format(weekStart, "MMM d")} - {format(weekEnd, "MMM d, yyyy")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button
              variant="outline"
              onClick={() => setShowCopyDialog(true)}
              className="border-[#E5E5E5] text-sm gap-1"
              data-testid="copy-week-btn"
            >
              <Copy className="w-4 h-4" />
              Copy Week
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentDate(subWeeks(currentDate, 1))}
            className="border-[#E5E5E5]"
            data-testid="prev-week"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => setCurrentDate(new Date())}
            className="border-[#E5E5E5] text-sm"
          >
            This Week
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCurrentDate(addWeeks(currentDate, 1))}
            className="border-[#E5E5E5]"
            data-testid="next-week"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Month Calendar Overview */}
      <Card className="border border-[#E5E5E5] shadow-none mb-4" data-testid="month-calendar">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="icon" onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))} className="h-7 w-7" data-testid="prev-month">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h3 className="text-sm font-semibold text-[#1A1A1A]">
              {format(calendarMonth, "MMMM yyyy")}
            </h3>
            <Button variant="ghost" size="icon" onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))} className="h-7 w-7" data-testid="next-month">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="grid grid-cols-7 gap-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="text-center text-xs font-medium text-[#999999] py-1">{d}</div>
            ))}
            {buildCalendarDays().map((day, idx) => {
              if (!day) return <div key={`empty-${idx}`} />;
              const dateStr = format(day, "yyyy-MM-dd");
              const count = monthlyCounts[dateStr] || 0;
              const isCurrentWeek = day >= weekStart && day <= weekEnd;
              return (
                <div
                  key={dateStr}
                  onClick={() => {
                    setCurrentDate(day);
                    setCalendarMonth(day);
                  }}
                  className={`text-center py-1.5 px-0.5 rounded cursor-pointer transition-all text-xs ${
                    count > 0
                      ? "bg-orange-100 border border-orange-300 hover:bg-orange-200"
                      : "hover:bg-[#F0EAEB]"
                  } ${isCurrentWeek ? "ring-1 ring-[#6A1E2E]" : ""} ${
                    isToday(day) ? "font-bold" : ""
                  }`}
                  data-testid={`cal-day-${dateStr}`}
                >
                  <div className={`${!isSameMonth(day, calendarMonth) ? "text-[#CCCCCC]" : "text-[#1A1A1A]"}`}>
                    {format(day, "d")}
                  </div>
                  {count > 0 && (
                    <div className="text-[9px] font-semibold text-orange-700 leading-tight">
                      {count} {count === 1 ? "EVENT" : "EVENTS"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-[#666666]">Loading schedule...</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
          {weekDays.map((day) => {
            const dayEvents = getShiftsForDate(day.date);
            const isToday = day.date === format(new Date(), "yyyy-MM-dd");

            return (
              <Card
                key={day.date}
                className={`border shadow-none ${
                  isToday ? "border-[#6A1E2E] ring-1 ring-[#6A1E2E]" : "border-[#E5E5E5]"
                }`}
                data-testid={`schedule-day-${day.dayName.toLowerCase()}`}
              >
                <CardHeader className="pb-2 pt-4 px-3">
                  <CardTitle className="text-center">
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#666666]">
                      {day.dayName}
                    </div>
                    <div className={`text-lg font-bold ${isToday ? "text-[#6A1E2E]" : "text-[#1A1A1A]"}`}>
                      {day.dayDate}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-2 pb-4 space-y-3 min-h-[250px]">
                  {dayEvents.length === 0 ? (
                    <p className="text-xs text-[#999999] text-center py-4">No shifts</p>
                  ) : (
                    dayEvents.map((event, eventIdx) => {
                      const hasOpenSlots = event.open_slots?.length > 0;
                      const myShiftIds = myShifts.map((s) => s.id);
                      const iHaveThisShift = event.all_shifts?.some(
                        (s) => s.assigned_to === user?.id
                      );

                      return (
                        <div
                          key={`${event.event_name}-${eventIdx}`}
                          className="border border-[#E5E5E5] rounded-sm overflow-hidden"
                        >
                          {/* Event Header */}
                          <div
                            className={`px-2 py-1.5 text-xs font-semibold ${
                              hasOpenSlots
                                ? "bg-[#E55934] text-white"
                                : "bg-[#6A1E2E] text-white"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="truncate flex-1">{event.event_name}</div>
                              <div className="flex items-center gap-0.5 shrink-0">
                                <button
                                  onClick={(e) => { e.stopPropagation(); openNotesDialog(event); }}
                                  className={`p-0.5 rounded hover:bg-white/20 relative ${event.notes_count > 0 ? "text-yellow-300" : "text-white/50"}`}
                                  data-testid={`notes-btn-${event.event_name}-${eventIdx}`}
                                >
                                  <MessageSquare className="w-3.5 h-3.5" />
                                  {event.notes_count > 0 && (
                                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-yellow-400 text-[#1A1A1A] rounded-full text-[8px] font-bold flex items-center justify-center">
                                      {event.notes_count}
                                    </span>
                                  )}
                                </button>
                                {canManage && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openEditEventDialog(event); }}
                                    className="p-0.5 rounded hover:bg-white/20 text-white/50 hover:text-white"
                                    data-testid={`edit-event-btn-${event.event_name}-${eventIdx}`}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {canManage && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteEventInfo({
                                        event_name: event.event_name,
                                        date: event.date,
                                        start_time: event.start_time,
                                        end_time: event.end_time,
                                        total_shifts: event.all_shifts?.length || 0,
                                        assigned_count: event.all_shifts?.filter(s => s.assigned_to).length || 0,
                                      });
                                      setShowDeleteEventDialog(true);
                                    }}
                                    className="p-0.5 rounded hover:bg-white/20 text-white/50 hover:text-white"
                                    data-testid={`delete-event-btn-${event.event_name}-${eventIdx}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="font-mono opacity-80">
                              {event.start_time}-{event.end_time}
                            </div>
                            {event.latest_note && (
                              <div className="mt-1 text-[10px] opacity-70 italic truncate">
                                {event.latest_note.text}
                              </div>
                            )}
                          </div>

                          {/* Employees by Position */}
                          <div className="p-2 space-y-2 bg-white">
                            {Object.entries(event.employees_by_position || {}).map(
                              ([position, employees]) => (
                                <div key={position}>
                                  <div className="text-[10px] font-semibold uppercase tracking-wider text-[#6A1E2E] mb-1">
                                    {position}
                                  </div>
                                  <div className="space-y-1">
                                    {employees.map((emp, empIdx) => {
                                      const shiftData = event.all_shifts?.find(
                                        (s) => s.id === emp.shift_id
                                      );
                                      const isMine = shiftData?.assigned_to === user?.id;

                                      return (
                                        <div
                                          key={empIdx}
                                          className={`text-xs px-1.5 py-1 rounded-sm flex items-center justify-between gap-1 transition-colors ${
                                            isMine
                                              ? "bg-[#6A1E2E] text-white"
                                              : "bg-[#F0EAEB] text-[#1A1A1A] hover:bg-[#E2D4D6]"
                                          } ${canManage ? "cursor-pointer" : ""}`}
                                          data-testid={`employee-shift-${emp.shift_id}`}
                                        >
                                          <span
                                            className="flex-1 truncate"
                                            onClick={(e) => {
                                              if (canManage) {
                                                handleEmployeeClick(e, emp.shift_id, emp.name, emp.position || position);
                                              } else if (!isMine && shiftData) {
                                                handleEmployeeShiftClick(event, {
                                                  ...shiftData,
                                                  shift_id: emp.shift_id,
                                                });
                                              }
                                            }}
                                          >
                                            {emp.name} - {emp.position || position}
                                            {isMine && " (You)"}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )
                            )}

                            {/* Open Slots - Orange highlighted, clickable */}
                            {hasOpenSlots && (
                              <div>
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#E55934] mb-1">
                                  Open ({event.open_slots.length})
                                </div>
                                {event.open_slots.map((slotId) => (
                                  <div
                                    key={slotId}
                                    className="text-xs px-1.5 py-1.5 rounded-sm flex items-center justify-between gap-1 transition-all bg-[#E55934] text-white hover:bg-[#D14A2A] mb-1 font-medium"
                                    data-testid={`open-shift-${slotId}`}
                                  >
                                    <span
                                      className="cursor-pointer flex-1 truncate"
                                      onClick={() => handleOpenShiftClick(event, slotId)}
                                    >
                                      {event.all_shifts?.find(s => s.id === slotId)?.positions_needed?.[0] || "OPEN"}
                                    </span>
                                    {canManage && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setDeleteShiftId(slotId); setShowDeleteDialog(true); }}
                                        className="shrink-0 p-0.5 rounded hover:bg-black/10 text-white/70 hover:text-white"
                                        data-testid={`delete-open-shift-${slotId}`}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* If no employees and no open slots */}
                            {Object.keys(event.employees_by_position || {}).length === 0 &&
                              !hasOpenSlots && (
                                <p className="text-[10px] text-[#999999]">No assignments</p>
                              )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-6 text-sm text-[#666666]">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#E55934] rounded-sm" />
          <span>Open Shift</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#6A1E2E] rounded-sm" />
          <span>My Shift / Event Header</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[#F0EAEB] border border-[#E5E5E5] rounded-sm" />
          <span>Other Employee</span>
        </div>
      </div>

      {/* Pickup Request Dialog (for Employees) */}
      <Dialog open={showPickupDialog} onOpenChange={setShowPickupDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Hand className="w-5 h-5 text-[#E55934]" />
              Request Open Shift
            </DialogTitle>
            <DialogDescription>
              Submit a request to pick up this open shift. An admin or supervisor will review your request.
            </DialogDescription>
          </DialogHeader>

          {selectedShift && (
            <div className="mt-3 p-3 bg-[#FEE2E2] border border-[#E55934]/30 rounded-sm">
              <div className="text-sm font-semibold text-[#1A1A1A]">
                {selectedShift.event_name}
              </div>
              <div className="text-xs text-[#666666] mt-1">
                {selectedShift.date && format(parseISO(selectedShift.date), "EEEE, MMM d")} |{" "}
                {selectedShift.start_time} - {selectedShift.end_time}
              </div>
              {selectedShift.positions_needed?.length > 0 && (
                <div className="text-xs text-[#666666] mt-1">
                  Positions: {selectedShift.positions_needed.join(", ")}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowPickupDialog(false)}
              className="border-[#E5E5E5]"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePickupRequest}
              disabled={submitting}
              className="bg-[#E55934] hover:bg-[#D14A2A] text-white"
              data-testid="confirm-pickup"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request This Shift"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Employee Dialog (for Admin/Supervisor) */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <User className="w-5 h-5 text-[#6A1E2E]" />
              Assign Employee to Shift
            </DialogTitle>
            <DialogDescription>
              Select an employee to assign to this open shift.
            </DialogDescription>
          </DialogHeader>

          {assignShiftInfo && (
            <div className="mt-3 p-3 bg-[#FEE2E2] border border-[#E55934]/30 rounded-sm">
              <div className="text-sm font-semibold text-[#1A1A1A]">
                {assignShiftInfo.event_name}
              </div>
              <div className="text-xs text-[#666666] mt-1">
                {assignShiftInfo.date && format(parseISO(assignShiftInfo.date), "EEEE, MMM d")} |{" "}
                {assignShiftInfo.start_time} - {assignShiftInfo.end_time}
              </div>
              {assignShiftInfo.positions?.length > 0 && (
                <div className="text-xs text-[#999999] mt-1">
                  Position: {assignShiftInfo.positions.join(", ")}
                </div>
              )}
            </div>
          )}

          {loadingSuggestions ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-[#6A1E2E]" />
              <span className="ml-2 text-sm text-[#666666]">Loading suggestions...</span>
            </div>
          ) : (
            <div className="space-y-3 mt-3">
              {suggestions.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#666666] mb-2">
                    Suggested (by availability & seniority)
                  </div>
                  <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                    {suggestions.filter(s => s.is_available && !s.has_conflicts).slice(0, 8).map((s) => (
                      <button
                        key={s.employee_id}
                        type="button"
                        onClick={() => setSelectedEmployee(s.employee_id)}
                        className={`w-full text-left p-2 rounded-sm border text-sm transition-colors ${
                          selectedEmployee === s.employee_id
                            ? "bg-[#6A1E2E] text-white border-[#6A1E2E]"
                            : "bg-white border-[#E5E5E5] hover:bg-[#F0EAEB]"
                        }`}
                        data-testid={`suggest-${s.employee_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{s.name}</span>
                          <span className={`text-xs ${selectedEmployee === s.employee_id ? "text-white/70" : "text-[#999999]"}`}>
                            {s.weekly_hours}h this week
                          </span>
                        </div>
                        {s.positions.length > 0 && (
                          <div className={`text-xs mt-0.5 ${selectedEmployee === s.employee_id ? "text-white/60" : "text-[#999999]"}`}>
                            {s.positions.join(", ")}
                          </div>
                        )}
                      </button>
                    ))}
                    {suggestions.filter(s => s.has_conflicts).length > 0 && (
                      <div className="text-xs text-[#999999] mt-2 mb-1 font-medium">With conflicts:</div>
                    )}
                    {suggestions.filter(s => s.has_conflicts).slice(0, 4).map((s) => (
                      <button
                        key={s.employee_id}
                        type="button"
                        onClick={() => setSelectedEmployee(s.employee_id)}
                        className={`w-full text-left p-2 rounded-sm border text-sm transition-colors ${
                          selectedEmployee === s.employee_id
                            ? "bg-yellow-600 text-white border-yellow-600"
                            : "bg-yellow-50 border-yellow-200 hover:bg-yellow-100"
                        }`}
                        data-testid={`suggest-conflict-${s.employee_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{s.name}</span>
                          <span className="text-xs text-yellow-700">conflict</span>
                        </div>
                        <div className="text-xs mt-0.5 text-yellow-700">
                          {s.conflicts.map(c => c.detail).join("; ")}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-[#666666] mb-2">
                  Or select any employee
                </div>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="border-[#E5E5E5]" data-testid="assign-employee-select">
                    <SelectValue placeholder="Choose an employee..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allEmployees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name} — {(emp.positions || []).join(", ") || "No position"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowAssignDialog(false)}
              className="border-[#E5E5E5]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignEmployee}
              disabled={submitting || !selectedEmployee}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="confirm-assign"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assign Employee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trade Dialog */}
      <Dialog open={showTradeDialog} onOpenChange={setShowTradeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-[#6A1E2E]" />
              Trade Shift Request
            </DialogTitle>
            <DialogDescription>
              Request to trade shifts with {selectedShift?.targetShift?.assigned_name}
            </DialogDescription>
          </DialogHeader>

          {selectedShift && (
            <div className="mt-3 p-3 bg-[#F0EAEB] rounded-sm">
              <div className="text-sm font-semibold text-[#1A1A1A]">
                Their Shift: {selectedShift.event_name}
              </div>
              <div className="text-xs text-[#666666] mt-1">
                {selectedShift.date && format(parseISO(selectedShift.date), "EEEE, MMM d")} |{" "}
                {selectedShift.start_time} - {selectedShift.end_time}
              </div>
            </div>
          )}

          <div className="space-y-3 mt-4">
            <label className="text-sm font-medium text-[#1A1A1A]">Select your shift to trade:</label>
            <Select value={selectedMyShift} onValueChange={setSelectedMyShift}>
              <SelectTrigger className="border-[#E5E5E5]" data-testid="select-my-shift">
                <SelectValue placeholder="Choose one of your shifts" />
              </SelectTrigger>
              <SelectContent>
                {myShifts.length === 0 ? (
                  <SelectItem value="none" disabled>
                    No shifts available to trade
                  </SelectItem>
                ) : (
                  myShifts.map((shift) => (
                    <SelectItem key={shift.id} value={shift.id}>
                      {shift.event_name} - {format(parseISO(shift.date), "MMM d")} ({shift.start_time}-{shift.end_time})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowTradeDialog(false)}
              className="border-[#E5E5E5]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleTradeSubmit}
              disabled={submitting || !selectedMyShift}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="submit-trade"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request Trade"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Shift Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              Delete Shift
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this shift? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeleteShiftId(null); }} className="border-[#E5E5E5]">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteShift}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-shift"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Entire Event Confirmation Dialog */}
      <Dialog open={showDeleteEventDialog} onOpenChange={setShowDeleteEventDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              Delete Entire Event
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>all shifts</strong> for this event, including assigned employees.
            </DialogDescription>
          </DialogHeader>

          {deleteEventInfo && (
            <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-sm space-y-2">
              <div className="text-sm font-semibold text-[#1A1A1A]">{deleteEventInfo.event_name}</div>
              <div className="text-xs text-[#666666]">
                {deleteEventInfo.date} | {deleteEventInfo.start_time} - {deleteEventInfo.end_time}
              </div>
              <div className="flex gap-4 text-xs mt-2">
                <span className="text-[#666666]">
                  Total shifts: <strong className="text-[#1A1A1A]">{deleteEventInfo.total_shifts}</strong>
                </span>
                {deleteEventInfo.assigned_count > 0 && (
                  <span className="text-red-600">
                    Assigned employees: <strong>{deleteEventInfo.assigned_count}</strong>
                  </span>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowDeleteEventDialog(false); setDeleteEventInfo(null); }} className="border-[#E5E5E5]">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteEvent}
              disabled={deletingEvent}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-event"
            >
              {deletingEvent ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              {deletingEvent ? "Deleting..." : "Delete All Shifts"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog — mirrors Add Event form */}
      <Dialog open={showEditEventDialog} onOpenChange={setShowEditEventDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#6A1E2E]" />
              Edit Event
            </DialogTitle>
            <DialogDescription>
              Update the event details. Assigned employees will be preserved.
            </DialogDescription>
          </DialogHeader>

          {editEventInfo && (
            <div className="space-y-5 mt-3">
              {/* Date info */}
              <div className="p-3 bg-[#F0EAEB] rounded-sm text-sm text-[#666666]">
                Date: <span className="font-semibold text-[#1A1A1A]">{editEventInfo.date}</span>
              </div>

              {/* Event Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#1A1A1A]">Event Name</label>
                <Input
                  value={editForm.event_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, event_name: e.target.value }))}
                  placeholder="e.g., Morning Tour, Night Security"
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="edit-event-name"
                />
              </div>

              {/* Time Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#1A1A1A]">Start Time (Military)</label>
                  <Input
                    type="time"
                    value={editForm.start_time}
                    onChange={(e) => setEditForm(prev => ({ ...prev, start_time: e.target.value }))}
                    className="border-[#E5E5E5] focus:border-[#6A1E2E] font-mono"
                    data-testid="edit-event-start"
                  />
                  {editForm.start_time && (
                    <p className="text-xs text-[#999999]">{editForm.start_time} hrs</p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#1A1A1A]">End Time (Military)</label>
                  <Input
                    type="time"
                    value={editForm.end_time}
                    onChange={(e) => setEditForm(prev => ({ ...prev, end_time: e.target.value }))}
                    className="border-[#E5E5E5] focus:border-[#6A1E2E] font-mono"
                    data-testid="edit-event-end"
                  />
                  {editForm.end_time && (
                    <p className="text-xs text-[#999999]">{editForm.end_time} hrs</p>
                  )}
                </div>
              </div>

              {/* Position Slots */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-[#1A1A1A] flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#6A1E2E]" />
                    Workers Needed by Position
                  </label>
                  {getEditTotalSlots() > 0 && (
                    <span className="text-sm font-semibold text-[#6A1E2E]">
                      Total: {getEditTotalSlots()} workers
                    </span>
                  )}
                </div>

                <div className="border border-[#E5E5E5] rounded-sm divide-y divide-[#E5E5E5]">
                  {editForm.position_slots.map((slot) => {
                    const assignedEmps = (slot.employees || []).filter(e => e.assigned_to);
                    const needsSelection = assignedEmps.length > slot.count && slot.count >= 0;
                    const keptAssigned = assignedEmps.filter(e => slot.keepIds?.includes(e.shift_id));

                    return (
                      <div key={slot.position}>
                        <div
                          className={`flex items-center justify-between p-3 ${
                            slot.count > 0 ? "bg-[#F0EAEB]" : "bg-white"
                          }`}
                        >
                          <span className={`font-medium text-sm ${slot.count > 0 ? "text-[#6A1E2E]" : "text-[#666666]"}`}>
                            {slot.position}
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 border-[#E5E5E5]"
                              onClick={() => handleEditPositionCount(slot.position, -1)}
                              disabled={slot.count === 0}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <Input
                              type="number"
                              min="0"
                              max="50"
                              value={slot.count}
                              onChange={(e) => setEditPositionCount(slot.position, e.target.value)}
                              className="w-16 text-center border-[#E5E5E5] font-mono"
                              data-testid={`edit-position-count-${slot.position.toLowerCase().replace(/\s+/g, "-")}`}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 border-[#E5E5E5]"
                              onClick={() => handleEditPositionCount(slot.position, 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Show assigned employees when reducing — let admin pick who stays */}
                        {assignedEmps.length > 0 && slot.count < assignedEmps.length && (
                          <div className="px-3 py-2 bg-yellow-50 border-t border-yellow-200">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-yellow-800 mb-1.5">
                              Select who to keep ({slot.count} of {assignedEmps.length}):
                            </div>
                            <div className="space-y-1">
                              {assignedEmps.map((emp) => {
                                const isKept = slot.keepIds?.includes(emp.shift_id);
                                return (
                                  <label
                                    key={emp.shift_id}
                                    className={`flex items-center gap-2 p-1.5 rounded-sm cursor-pointer text-xs transition-colors ${
                                      isKept ? "bg-green-50 border border-green-200" : "bg-white border border-[#E5E5E5] opacity-60"
                                    }`}
                                    data-testid={`keep-employee-${emp.shift_id}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isKept}
                                      onChange={() => toggleKeepEmployee(slot.position, emp.shift_id)}
                                      className="rounded border-[#E5E5E5] text-[#6A1E2E]"
                                    />
                                    <span className={`font-medium ${isKept ? "text-[#1A1A1A]" : "text-[#999999] line-through"}`}>
                                      {emp.name || "Open Slot"}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                            {keptAssigned.length > slot.count && (
                              <div className="text-[10px] text-red-600 mt-1 font-medium">
                                Too many selected — deselect {keptAssigned.length - slot.count} more
                              </div>
                            )}
                          </div>
                        )}

                        {/* Show assigned employees as info when NOT reducing */}
                        {assignedEmps.length > 0 && slot.count >= assignedEmps.length && (
                          <div className="px-3 py-1.5 bg-green-50/50 border-t border-green-100">
                            <div className="text-[10px] text-green-700">
                              {assignedEmps.map(e => e.name).filter(Boolean).join(", ")} — will be kept
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {editForm.position_slots.length === 0 && (
                    <div className="p-4 text-center text-[#999999] text-sm">
                      No positions available.
                    </div>
                  )}
                </div>

                {/* Active positions summary */}
                {getEditActivePositions().length > 0 && (
                  <div className="text-sm text-[#666666] p-2 bg-[#FAFAFA] rounded-sm">
                    <span className="font-medium">Summary: </span>
                    {getEditActivePositions().map((slot, idx) => (
                      <span key={slot.position}>
                        {slot.count} {slot.position}
                        {idx < getEditActivePositions().length - 1 ? ", " : ""}
                      </span>
                    ))}
                    <span className="ml-1 text-[#6A1E2E] font-semibold">= {getEditTotalSlots()} total shifts</span>
                  </div>
                )}
              </div>

              {/* Info about employee preservation */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-800">
                Assigned employees will be preserved. If reducing slots, select which employees to keep above.
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowEditEventDialog(false); setEditEventInfo(null); }} className="border-[#E5E5E5]">
              Cancel
            </Button>
            <Button
              onClick={handleSaveEvent}
              disabled={savingEvent || getEditTotalSlots() === 0}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="confirm-edit-event"
            >
              {savingEvent ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Pencil className="w-4 h-4 mr-1" />}
              {savingEvent ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#6A1E2E]" />
              Shift Notes
            </DialogTitle>
            <DialogDescription>{notesEventName}</DialogDescription>
          </DialogHeader>

          {/* Add Note (admin/supervisor only) */}
          {canManage && (
            <div className="flex gap-2 mt-2">
              <Input
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                placeholder="Add a note (e.g., Bring rain gear, VIP group)..."
                className="border-[#E5E5E5] text-sm flex-1"
                onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
                data-testid="note-input"
              />
              <Button
                onClick={handleAddNote}
                disabled={addingNote || !newNoteText.trim()}
                size="sm"
                className="bg-[#6A1E2E] hover:bg-[#521422] text-white h-9 px-3"
                data-testid="add-note-btn"
              >
                {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          )}

          {/* Notes List */}
          <div className="space-y-2 mt-3">
            {loadingNotes ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-[#6A1E2E]" />
              </div>
            ) : shiftNotes.length === 0 ? (
              <p className="text-sm text-[#999999] text-center py-6">No notes yet</p>
            ) : (
              shiftNotes.map((note) => (
                <div key={note.id} className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm" data-testid={`note-${note.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm text-[#1A1A1A]">{note.text}</p>
                      <div className="text-[10px] text-[#999999] mt-1">
                        {note.author_name} — {new Date(note.created_at).toLocaleString()}
                      </div>
                    </div>
                    {canManage && (
                      <button
                        onClick={() => handleDeleteNote(note.id, note.shift_id)}
                        className="text-[#999999] hover:text-red-600 p-0.5"
                        data-testid={`delete-note-${note.id}`}
                      >
                        <XIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Context Menu for Admin */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-white border border-[#E5E5E5] rounded-sm shadow-lg min-w-[200px] py-1"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
          data-testid="employee-context-menu"
        >
          <div className="px-3 py-2 border-b border-[#E5E5E5]">
            <div className="text-xs font-semibold text-[#1A1A1A]">{contextMenu.empName}</div>
            <div className="text-[10px] text-[#666666]">{contextMenu.position}</div>
          </div>
          {!showRemoveSubmenu ? (
            <>
              <button
                onClick={() => setShowRemoveSubmenu(true)}
                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                data-testid="remove-from-tour-btn"
              >
                <UserMinus className="w-4 h-4" />
                Remove from Tour
              </button>
              <button
                onClick={() => { setDeleteShiftId(contextMenu.shiftId); setShowDeleteDialog(true); setContextMenu(null); }}
                className="w-full text-left px-3 py-2 text-sm text-[#666666] hover:bg-[#F5F5F5] flex items-center gap-2 transition-colors"
                data-testid="delete-shift-menu-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete Shift
              </button>
            </>
          ) : (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#999999] border-b border-[#E5E5E5]">
                Select Reason
              </div>
              {REMOVAL_REASONS.map((reason) => (
                <button
                  key={reason.value}
                  onClick={() => handleRemoveFromTour(reason.value)}
                  disabled={removingShiftId === contextMenu.shiftId}
                  className="w-full text-left px-3 py-2 text-sm text-[#1A1A1A] hover:bg-[#F0EAEB] transition-colors"
                  data-testid={`remove-reason-${reason.value.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {removingShiftId === contextMenu.shiftId ? (
                    <Loader2 className="w-3 h-3 animate-spin inline mr-2" />
                  ) : null}
                  {reason.label}
                </button>
              ))}
              <button
                onClick={() => setShowRemoveSubmenu(false)}
                className="w-full text-left px-3 py-1.5 text-xs text-[#999999] hover:bg-[#F5F5F5] border-t border-[#E5E5E5]"
              >
                Back
              </button>
            </>
          )}
        </div>
      )}

      {/* Copy Week Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Copy className="w-5 h-5 text-[#6A1E2E]" />
              Copy Week Schedule
            </DialogTitle>
            <DialogDescription>
              Copy all shifts from <strong>{format(weekStart, "MMM d")} - {format(weekEnd, "MMM d")}</strong> to future weeks. Assigned employees will be carried over.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#1A1A1A]">Copy to how many future weeks?</label>
              <Select value={String(copyTargetWeeks)} onValueChange={(v) => setCopyTargetWeeks(parseInt(v))}>
                <SelectTrigger className="border-[#E5E5E5]" data-testid="copy-weeks-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} week{n > 1 ? "s" : ""} — through {format(addWeeks(weekEnd, n), "MMM d")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-[#F0EAEB] rounded-sm text-sm text-[#6A1E2E]">
              This will duplicate {groupedShifts.length > 0 ? "all" : "no"} events from the current week view to {copyTargetWeeks} future week{copyTargetWeeks > 1 ? "s" : ""}.
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCopyDialog(false)} className="border-[#E5E5E5]">Cancel</Button>
            <Button
              onClick={handleCopyWeek}
              disabled={copying || groupedShifts.length === 0}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="confirm-copy-week"
            >
              {copying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copying ? "Copying..." : "Copy Schedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanySchedule;
